<template>
    <footer
        id="ReaderFooter"
        ref="ReaderFooter"
        dusk="ReaderFooter"
        role="contentinfo"
        :inert="uiHidden"
        :class="[
            'ReaderFooter',
            'flex',
            'items-center',
            'group',
            'fixed',
            'bottom-0',
            'select-none',
            'text-theme-text',
            'pt-4',
            'hover:opacity-100',
            'z-10',
            'rounded-t-lg',
            { 'bg-theme-bg': !uiHidden },
        ]"
    >
        <Container>
            <transition name="reader-footer-row" v-on="rowTransitionListeners">
                <div v-if="isOffline">
                    <Row>
                        <LayoutCol :class="{ 'mb-2': !uiHidden }">
                            <ComponentNetworkStatus />
                        </LayoutCol>
                    </Row>
                </div>
            </transition>
            <Row>
                <LayoutCol class="px-4 md:px-0">
                    <transition name="fade-transition">
                        <ReadProgress v-show="!hideReadProgress" />
                    </transition>
                    <ComponentRangePaginator
                        v-if="showRangePaginator"
                        :class="['transition-opacity', 'duration-200', paginatorOpacityClass]"
                    />
                </LayoutCol>
            </Row>
            <transition name="reader-footer-row" v-on="rowTransitionListeners">
                <div v-if="onlyMobileVisible && !uiHidden">
                    <Row class="pb-4">
                        <LayoutCol class="px-4">
                            <PdfEpubActionsFooter class="text-theme-icon" />
                        </LayoutCol>
                    </Row>
                </div>
            </transition>
        </Container>
    </footer>
</template>

<script>
'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';
import { observeElementResize } from '_shared_/js/utils/observeElementResize.js';
import { measureReservedHeight } from '_shared_/js/utils/measureReservedHeight.js';
import ComponentRangePaginator from './Components/ComponentRangePaginator.vue';
import ReadProgress from './Components/ReadProgress.vue';
import PdfEpubActionsFooter from './Footer/PdfEpubActionsFooter.vue';
import ComponentNetworkStatus from './Components/ComponentNetworkStatus.vue';

export default {
    components: {
        ComponentRangePaginator,
        ReadProgress,
        PdfEpubActionsFooter,
        ComponentNetworkStatus,
    },
    data() {
        return {
            footerReservedHeight: 0,
            transitioningRowCount: 0,
        };
    },
    computed: {
        rowTransitionListeners() {
            return {
                'before-enter': this.prepareExpand,
                enter: this.expand,
                'after-enter': this.clearHeight,
                'enter-cancelled': this.clearHeight,
                'before-leave': this.prepareCollapse,
                leave: this.collapse,
                'after-leave': this.clearHeight,
                'leave-cancelled': this.clearHeight,
            };
        },
        ...mapState(['uiHidden', 'isOffline']),
        ...mapState({ footerShownHeight: state => state.ui.toolbar.shownHeight }),
        ...mapGetters(['showRangePaginator', 'onlyMobileVisible', 'scrollPageMode']),
        hideReadProgress() {
            // Scroll runs content under the footer, so the readout gives way there.
            // Elsewhere it stays up: with the controls away it is the only position
            // cue left, and the clearance already reserves its height.
            return this.uiHidden && this.scrollPageMode;
        },
        paginatorOpacityClass() {
            // Visible only alongside the reader controls: fully faded (and
            // untappable) while the UI is hidden, without collapsing its
            // layout space so the content clearance stays stable.
            return this.uiHidden ? 'pointer-events-none opacity-0' : 'opacity-100';
        },
    },
    watch: {
        footerReservedHeight: 'recordFooterHeight',
        transitioningRowCount: 'recordFooterHeight',
        uiHidden() {
            // Returning controls bring the mobile actions row in through a
            // transition whose before-enter hook runs during render, after this
            // watcher. Recording after render counts that row as moving, so the
            // reserve holds instead of adopting the collapsed height for a frame.
            this.$nextTick(() => {
                if (!this._isDestroyed) this.recordFooterHeight();
            });
        },
    },
    mounted() {
        const footer = this.$refs.ReaderFooter;
        this.stopObservingResize = observeElementResize(footer, () => {
            this.footerReservedHeight = measureReservedHeight(footer);
        });
    },
    beforeDestroy() {
        this.stopObservingResize();
        // Reader.vue drops the footer once a manifest says audiobook. A footer
        // that is gone reserves nothing.
        this.setFooterbarOffsetHeight(0);
        this.setFooterbarShownHeight(0);
    },
    methods: {
        prepareExpand(element) {
            this.transitioningRowCount += 1;
            element.style.height = '0px';
        },
        expand(element) {
            void element.offsetHeight;
            element.style.height = `${element.scrollHeight}px`;
        },
        prepareCollapse(element) {
            this.transitioningRowCount += 1;
            element.style.height = `${element.getBoundingClientRect().height}px`;
            void element.offsetHeight;
        },
        collapse(element) {
            element.style.height = '0px';
        },
        clearHeight(element) {
            element.style.height = '';
            this.transitioningRowCount -= 1;
        },
        ...mapMutations(['setFooterbarOffsetHeight', 'setFooterbarShownHeight']),
        recordFooterHeight() {
            const height = this.footerReservedHeight;
            this.setFooterbarOffsetHeight(height);
            if (this.uiHidden) return;
            // While a row is mid-transition the reserve only grows. The settled
            // box is the one reading allowed to shrink it.
            if (!this.transitioningRowCount || height > this.footerShownHeight) {
                this.setFooterbarShownHeight(height);
            }
        },
    },
};
</script>
<style>
.ReaderFooter {
    left: calc(var(--sidebar-offset-left, 0px) + 16px);
    right: calc(var(--sidebar-offset-right, 0px) + 16px);
    transition:
        left 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        right 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        opacity 0.2s ease-out,
        background-color 0.2s ease-out;
}

@media screen and (max-width: 570px) {
    .ReaderFooter {
        left: 0;
        right: 0;
    }
}
</style>

<style scoped>
.reader-footer-row-enter-active,
.reader-footer-row-leave-active {
    overflow: hidden;
    transition:
        height 200ms ease-out,
        opacity 200ms ease-out;
}

.reader-footer-row-enter,
.reader-footer-row-leave-to {
    opacity: 0;
}

.reader-footer-row-enter-to,
.reader-footer-row-leave {
    opacity: 1;
}
</style>
